import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Box,
  Center,
  Divider,
  Heading,
  HStack,
  Image,
  Pressable,
  Spinner,
  Stack,
  Text,
  useDisclose,
  VStack,
} from "native-base";
import { Helmet } from "react-helmet-async";
import { FiEdit } from "react-icons/fi";
import { TbBus } from "react-icons/tb";
import { Link, useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import ImageGallery from "react-image-gallery";
import { io } from "socket.io-client";
import styles from "./styles.module.css";
import Itinerary from "../../../components/itinerary";
import seat from "../../../assets/icons/seat.svg";
import colors from "../../../constants/colors";
import routes from "../../../routes";
import addCircle from "../../../assets/icons/add-circle.svg";
import removeCircle from "../../../assets/icons/remove-circle.svg";
import cash from "../../../assets/icons/cash.svg";
import Cell from "../../../components/cell";
import API, { baseURL, getFileUrl } from "../../../api";
import { formatToCurrency, getOriginalPrice } from "../../../utils";
import { StateContext, StatusContext } from "../../../contexts";
import QuotationFinishedModal from "../../../components/quotationFinishedModal";
import PaymentModal from "../../../components/paymentModal";
import Modal from "../../../components/modal";
import PendingPaymentModal from "../../../components/pendingPaymentModal";
import UnitsModal from "../../../components/unitsModal";
import DownloadQuotationButton from "../../../components/downloadQuotationButton";
import { useRef } from "react";
import CreateAccountModal from "../../../components/createAccountModal";

function ExperienceCreationPurchaseSummaryPage() {
  const params = useParams();
  const navigate = useNavigate();
  const state = useContext(StateContext);
  const status = useContext(StatusContext);
  const {
    isOpen: isOpenPaymentModal,
    onOpen: onOpenPaymentModal,
    onClose: onClosePaymentModal,
  } = useDisclose();
  const { isOpen: isOpenInfoModal, onClose: onCloseInfoModal } =
    useDisclose(true);
  const [itinerary, setItinerary] = useState([]);
  const [results, setResults] = useState([]);
  const [isPartialPayment, setIsPartialPayment] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [finished, setFinished] = useState(false);
  const [modaImages, setModaImages] = useState([]);
  const {
    isOpen: isPendingPaymentModalOpen,
    onOpen: onOpenPendingPaymentModal,
    onClose: onClosePendingPaymentModal,
  } = useDisclose();
  const [numberOfPassengers, setNumberOfPassengers] = useState(0);
  const [maximumUnits, setMaximumUnits] = useState(2);
  const {
    isOpen: isUnitsModalOpen,
    onClose: onCloseUnitsModal,
    onOpen: onOpenUnitsModal,
  } = useDisclose();
  const [isSaving, setIsSaving] = useState(false);
  const socketRef = useRef();
  const {
    isOpen: isCreateAccountModal,
    onOpen: onOpenCreateAccount,
    onClose: onCloseCreateAccount,
  } = useDisclose();

  const getQuotation = useCallback(async () => {
    try {
      setIsLoading(true);
      const {
        data: {
          data: {
            itinerary: _itinerary,
            numberOfPassengers: _numberOfPassengers,
            maximumUnits: _maximumUnits,
            results: _results,
          },
        },
      } = await API(state.jwt).get(`/quotations/${params.id}`);
      setItinerary(_itinerary);
      setNumberOfPassengers(_numberOfPassengers);
      setMaximumUnits(_maximumUnits);
      setResults(_results);
    } catch (error) {
      if (error?.response?.data?.error?.details?.finished) {
        setFinished(true);
      }
      if (error?.response?.status === 403) {
        navigate(routes.experienceCreation);
      }
      status.updateErrorMessage(error);
    }
    setIsLoading(false);
  }, [state.jwt, params.id, status, navigate]);

  useEffect(() => {
    getQuotation();
  }, [getQuotation]);

  const onPayTour = useCallback(
    async (type, _, __, onError) => {
      try {
        const { data } = await API(state.jwt).post(
          `/quotations/${params.id}/payment`,
          { data: { results, isPartialPayment, type } },
        );
        if (type === "card") {
          window.open(data.url, "_blank", "noopener,noreferrer");
          socketRef.current = io(baseURL);
          await new Promise((resolve) =>
            socketRef.current.on("connect", resolve),
          );
          onOpenPendingPaymentModal();
          socketRef.current.on(`payment:${data.id}`, (payment) => {
            socketRef.current.close();
            if (payment.status === "approved") {
              onClosePendingPaymentModal();
              navigate({
                pathname: routes.trips,
                search: `tour=${payment.id}`,
              });
            } else {
              onClosePendingPaymentModal();
              status.updateWarningMessage("Pago rechazado, intente nuevamente");
            }
          });
        } else if (type === "balance") {
          navigate({
            pathname: routes.trips,
            search: `tour=${data.id}`,
          });
        }
        onClosePaymentModal();
      } catch (error) {
        onError();
        status.updateErrorMessage(error);
      }
    },
    [
      state.jwt,
      params.id,
      results,
      isPartialPayment,
      onClosePaymentModal,
      onOpenPendingPaymentModal,
      onClosePendingPaymentModal,
      navigate,
      status,
    ],
  );

  const onAdd = useCallback(
    (index) => {
      let _results = [...results];
      if (
        _results.reduce((total, result) => total + result.quantity, 0) >=
        maximumUnits
      ) {
        onOpenUnitsModal();
        return;
      }
      _results = _results.map((_result, _index) => ({
        ..._result,
        quantity: _result.quantity + (index === _index ? 1 : 0),
      }));
      setResults(_results);
    },
    [results, maximumUnits, onOpenUnitsModal],
  );

  const onRemove = useCallback(
    (index) => {
      let _results = [...results];
      _results = _results.map((_result, _index) => ({
        ..._result,
        quantity:
          _result.quantity > 0
            ? _result.quantity - (index === _index ? 1 : 0)
            : 0,
      }));
      setResults(_results);
    },
    [results],
  );

  const onRequestUnits = useCallback(async () => {
    try {
      setIsSaving(true);
      await API(state.jwt).put(`/quotations/${params.id}`, {
        data: { requestUnits: true },
      });
      status.updateSuccessMessage(
        "Solicitud enviada correctamente, Dentro de 24 a 48 horas recibirá respuesta de su solicitud",
      );
      onCloseUnitsModal();
    } catch (error) {
      status.updateErrorMessage(error);
    }
    setIsSaving(false);
  }, [onCloseUnitsModal, params.id, state.jwt, status]);

  const total = useMemo(
    () =>
      results.reduce(
        (total, result) => total + result.price * result.quantity,
        0,
      ),
    [results],
  );

  const origin = useMemo(
    () =>
      itinerary.find(
        (location) => location.type === "origin" && !location.isReturn,
      ) || {},
    [itinerary],
  );

  const totalNumberOfPassengers = useMemo(
    () =>
      results.reduce(
        (total, result) =>
          total + result.quantity * result.vehicleType.capacity,
        0,
      ),
    [results],
  );

  const disabled = useMemo(
    () => totalNumberOfPassengers < numberOfPassengers,
    [numberOfPassengers, totalNumberOfPassengers],
  );

  const closingPendingPaymentModal = useCallback(() => {
    socketRef.current.close();
    onClosePendingPaymentModal();
    navigate(routes.trips);
  }, [navigate, onClosePendingPaymentModal]);

  const noToll = useMemo(
    () => results.some((result) => result.tolls === -1),
    [results],
  );

  const hoursToDepartureDate = useMemo(
    () => moment(origin.departureDate).diff(moment(), "hours"),
    [origin.departureDate],
  );

  return (
    <VStack paddingX={5}>
      <Helmet>
        <title>Selección de vehículo</title>
      </Helmet>
      <HStack space={3} alignItems="flex-start">
        <VStack flex={1} space={5} maxWidth={300} width="full">
          <Heading color={colors.secondary} fontSize="2xl">
            Resumen de compra
          </Heading>
          <VStack backgroundColor={colors.white} padding={5}>
            {isLoading ? (
              <Center height={300}>
                <Spinner size="lg" color={colors.primary} />
              </Center>
            ) : (
              <Itinerary itinerary={itinerary} />
            )}
          </VStack>
        </VStack>
        <VStack flex={1} space={5}>
          <HStack alignItems="flex-start" justifyContent="space-between">
            <HStack space={4} flex={1}>
              <Heading color={colors.secondary} fontSize="2xl">
                Resumen de tu viaje
              </Heading>
              <HStack space={2} flex={1} alignItems="flex-start">
                <Image src={seat} alt="seat" width={5} height={5} />
                <Text color={colors.secondary}>{numberOfPassengers}</Text>
              </HStack>
            </HStack>
            <DownloadQuotationButton
              hideDiscount
              id={params.id}
              disabled={isLoading}
              itinerary={itinerary}
              results={results}
            />
          </HStack>
          {isLoading ? (
            <Center height={300}>
              <Spinner size="lg" color={colors.primary} />
            </Center>
          ) : (
            <VStack backgroundColor={colors.white} paddingX={1} paddingY={5}>
              <HStack space={3} marginBottom={5} paddingX={2}>
                <TbBus color={colors.secondary} size={30} />
                <Heading color={colors.secondary} fontSize="xl" flex={1}>
                  Vehículos
                </Heading>
                <Link
                  to={routes.experienceCreationId.replace(":id", params.id)}>
                  <HStack
                    space={3}
                    paddingX={4}
                    paddingY={2}
                    borderColor={colors.primary}
                    borderWidth={1}
                    borderRadius="md">
                    <FiEdit color={colors.primary} size={20} />
                    <Text bold color={colors.primary}>
                      Editar
                    </Text>
                  </HStack>
                </Link>
              </HStack>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <Cell>
                      <Text bold color={colors.secondary}>
                        Tipo de
                        <br />
                        unidad
                      </Text>
                    </Cell>
                    <Cell bold color={colors.secondary}>
                      Capacidad
                    </Cell>
                    <Cell bold color={colors.secondary}>
                      Amenidades
                    </Cell>
                    <Cell bold color={colors.secondary}>
                      Fotos
                    </Cell>
                    <Cell>
                      <Text bold color={colors.secondary}>
                        # de
                        <br />
                        unidades
                      </Text>
                    </Cell>
                    <Cell>
                      <Text bold color={colors.secondary}>
                        Precio
                        <br />
                        unitario
                      </Text>
                    </Cell>
                    <Cell bold color={colors.secondary}>
                      A pagar
                    </Cell>
                  </tr>
                </thead>
                <tbody>
                  {results.map((result, index) => (
                    <tr key={String(index)}>
                      <td>
                        <Text bold fontSize="sm">
                          {result.vehicleType.name}
                        </Text>
                      </td>
                      <td>
                        <Text bold fontSize="sm">
                          {result.vehicleType.capacity}
                        </Text>
                      </td>
                      <td>
                        <HStack space={1} justifyContent="center">
                          {result.vehicleType.amenities
                            .slice(0, 3)
                            .map((amenity) => (
                              <VStack
                                key={amenity.id}
                                alignItems="center"
                                space={1}>
                                <Image
                                  src={getFileUrl(amenity.image.url)}
                                  alt="vehicle"
                                  width={5}
                                  height={5}
                                />
                                <Text fontSize="sm" lineHeight="sm">
                                  {amenity.name}
                                </Text>
                              </VStack>
                            ))}
                        </HStack>
                      </td>
                      <td>
                        {Boolean(result.vehicleType.photos.length > 0) && (
                          <Pressable
                            onPress={() =>
                              setModaImages(result.vehicleType.photos)
                            }>
                            <Text underline color={colors.primary}>
                              Ver fotos
                            </Text>
                          </Pressable>
                        )}
                      </td>
                      <td>
                        <HStack space={2}>
                          <Pressable
                            onPress={() => onRemove(index)}
                            disabled={result.quantity === 0}>
                            <Image
                              src={removeCircle}
                              alt="remove-circle"
                              width={4}
                              height={4}
                            />
                          </Pressable>
                          <Text fontSize="sm">{result.quantity}</Text>
                          <Pressable onPress={() => onAdd(index)}>
                            <Image
                              src={addCircle}
                              alt="add-circle"
                              width={4}
                              height={4}
                            />
                          </Pressable>
                        </HStack>
                      </td>
                      <td>
                        <Text bold fontSize="sm">
                          {formatToCurrency(
                            getOriginalPrice(result.price, result.coupon),
                          )}
                        </Text>
                      </td>
                      <td>
                        <Text bold fontSize="sm">
                          {formatToCurrency(result.price * result.quantity)}
                        </Text>
                      </td>
                    </tr>
                  ))}
                  <tr style={{ backgroundColor: colors.mail }}>
                    <td>
                      <Text bold fontSize="sm" color={colors.white}>
                        Total
                      </Text>
                    </td>
                    <td>
                      <Text bold fontSize="sm" color={colors.white}>
                        {results.reduce(
                          (total, result) =>
                            total +
                            result.vehicleType.capacity * result.quantity,
                          0,
                        )}
                      </Text>
                    </td>
                    <td />
                    <td />
                    <td>
                      <Text bold fontSize="sm" color={colors.white}>
                        {results.reduce(
                          (total, result) => total + result.quantity,
                          0,
                        )}
                      </Text>
                    </td>
                    <td />
                    <td>
                      <Text bold fontSize="sm" color={colors.white}>
                        {formatToCurrency(
                          results.reduce(
                            (total, result) =>
                              total + result.price * result.quantity,
                            0,
                          ),
                        )}
                      </Text>
                    </td>
                  </tr>
                </tbody>
              </table>
              {totalNumberOfPassengers > 0 &&
                totalNumberOfPassengers < numberOfPassengers && (
                  <Text textAlign="end" marginTop={1}>
                    Capacidad de unidades seleccionadas no cubre el número de
                    pasajeros
                  </Text>
                )}
              <VStack paddingX={5} space={5}>
                <HStack paddingTop={10} space={5}>
                  <HStack>
                    <Text bold color={colors.secondary}>
                      Subtotal:
                    </Text>
                    <Text color={colors.lightGray}>
                      {" "}
                      {formatToCurrency(total / 1.16)}
                    </Text>
                  </HStack>
                  <HStack>
                    <Text bold color={colors.secondary}>
                      IVA:
                    </Text>
                    <Text color={colors.lightGray}>
                      {" "}
                      {formatToCurrency((1 - 1 / 1.16) * total)}
                    </Text>
                  </HStack>
                  <HStack>
                    <Text bold color={colors.secondary}>
                      Total:
                    </Text>
                    <Text color={colors.lightGray}>
                      {" "}
                      {formatToCurrency(total)}
                    </Text>
                  </HStack>
                </HStack>
                <Center>
                  <Link
                    to={routes.experienceCreationComplements.replace(
                      ":id",
                      params.id,
                    )}>
                    <Text color={colors.third} underline>
                      Complementos de viaje
                    </Text>
                  </Link>
                </Center>
              </VStack>
              <Divider backgroundColor={colors.primary} marginY={10} />
              <HStack space={4} paddingX={5} alignItems="flex-start">
                <Image src={cash} alt="cash" width={30} height={30} />
                <VStack flex={1} space={3}>
                  <Heading color={colors.secondary} fontSize="2xl">
                    Pagar Totalidad
                  </Heading>
                  <Text bold color={colors.secondary}>
                    Liquida tu viaje al 100%
                  </Text>
                  <HStack>
                    <Text bold color={colors.secondary}>
                      Renta de vehículos
                    </Text>
                    <Text> {formatToCurrency(total)}</Text>
                  </HStack>
                  <HStack>
                    <Text bold color={colors.secondary}>
                      Total general
                    </Text>
                    <Text> {formatToCurrency(total)}</Text>
                  </HStack>
                </VStack>
                <Pressable
                  alignSelf="flex-end"
                  onPress={() => {
                    if (state.user.id) {
                      setIsPartialPayment(false);
                      onOpenPaymentModal();
                    } else {
                      onOpenCreateAccount();
                    }
                  }}
                  disabled={disabled}>
                  <Stack
                    minWidth={200}
                    paddingX={5}
                    paddingY={3}
                    backgroundColor={colors.primary}
                    alignItems="center"
                    borderRadius={5}>
                    <Text color={colors.white}>Liquidar</Text>
                  </Stack>
                </Pressable>
              </HStack>
              <Divider backgroundColor={colors.primary} marginY={10} />
              <HStack space={4} paddingX={5} alignItems="flex-start">
                <Image src={cash} alt="cash" width={30} height={30} />
                <VStack flex={1} space={3}>
                  <Heading color={colors.secondary} fontSize="2xl">
                    Pagar Anticipo
                  </Heading>
                  <HStack>
                    <Text bold color={colors.secondary}>
                      Reserva tu viaje con un anticipo del 50%, deberá estar
                      pagado en su totalidad 3 días antes de la salida.
                    </Text>
                  </HStack>
                  <HStack>
                    <Text bold color={colors.secondary}>
                      Saldo pendiente de liquidar antes del{" "}
                      {moment(origin.departureDate)
                        .subtract(3, "days")
                        .format("DD [de] MMMM Y")}
                    </Text>
                    <Text> {formatToCurrency(total * 0.5)}</Text>
                  </HStack>
                </VStack>
                <Pressable
                  alignSelf="flex-end"
                  onPress={() => {
                    if (state.user.id) {
                      setIsPartialPayment(true);
                      onOpenPaymentModal();
                    } else {
                      onOpenCreateAccount();
                    }
                  }}
                  disabled={
                    disabled ||
                    hoursToDepartureDate < moment.duration(6, "days").asHours()
                  }>
                  <Stack
                    minWidth={200}
                    paddingX={5}
                    paddingY={3}
                    backgroundColor={colors.primary}
                    alignItems="center"
                    borderRadius={5}>
                    <Text color={colors.white}>
                      Abonar {formatToCurrency(total * 0.5)}
                    </Text>
                  </Stack>
                </Pressable>
              </HStack>
              {hoursToDepartureDate < moment.duration(6, "days").asHours() && (
                <Text marginRight={5} marginTop={3} textAlign="right">
                  Solo es posible si tu viaje es dentro de 7 días o más
                </Text>
              )}
            </VStack>
          )}
        </VStack>
      </HStack>
      <PaymentModal
        isOpen={isOpenPaymentModal}
        onRequestClose={onClosePaymentModal}
        label={
          isPartialPayment
            ? "Anticipo por el 50% del total de la venta"
            : "Total a pagar"
        }
        amount={isPartialPayment ? total * 0.5 : total}
        onPay={onPayTour}
        role="user"
      />
      <QuotationFinishedModal isOpen={finished} role="user" />
      <Modal
        maxWidth={900}
        isOpen={modaImages.length > 0}
        onRequestClose={() => setModaImages([])}>
        <VStack paddingX={5} paddingY={7}>
          <Heading color={colors.secondary} fontSize="2xl">
            Fotos del vehículo
          </Heading>
          <ImageGallery
            items={modaImages.map((image) => ({
              original: getFileUrl(image.url),
              thumbnail: getFileUrl(
                image?.formats?.thumbnail?.url || image.url,
              ),
              thumbnailHeight: 90,
              thumbnailWidth: 90,
            }))}
            showThumbnails
            showPlayButton={false}
          />
        </VStack>
      </Modal>
      <PendingPaymentModal
        isOpen={isPendingPaymentModalOpen}
        onRequestClose={closingPendingPaymentModal}
      />
      <UnitsModal
        isOpen={isUnitsModalOpen}
        onRequestClose={isSaving ? null : onCloseUnitsModal}
        maximumUnits={maximumUnits}
        onRequestUnits={onRequestUnits}
        isSaving={isSaving}
      />
      <CreateAccountModal
        isOpen={isCreateAccountModal}
        onRequestClose={onCloseCreateAccount}
      />
      <Modal isOpen={isOpenInfoModal && !isLoading}>
        <VStack padding={5} space={5} textAlign="center">
          <Text textAlign="center">
            {noToll
              ? "Actualmente no podemos estimar el costo de las casetas, el precio final del viaje puede variar. Uno de nuestros ejecutivos se pondrá en contacto contigo en las próximas 24hrs y te proporcionara la tarifa final."
              : "La tarifa brindada incluye todos los destinos informados, cualquier modificación en el itinerario de tu viaje, la tarifa sufrirá cambios."}
          </Text>
          <Pressable onPress={onCloseInfoModal} flex={1} disabled={isSaving}>
            <Box
              backgroundColor={colors.primary}
              paddingY={2}
              paddingX={8}
              borderRadius="full">
              <Text color={colors.white} textAlign="center">
                Aceptar
              </Text>
            </Box>
          </Pressable>
        </VStack>
      </Modal>
    </VStack>
  );
}

export default ExperienceCreationPurchaseSummaryPage;
